import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PublicShareForm } from './constructor/shared/PublicShareForm'
import { PaymentSettingsField } from '../payments/PaymentSettingsField'
import { Button } from '@unified-lk/ui'
import type { PublicTreatmentPlan } from '@unified-lk/api-contracts'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { StaffShell } from '../StaffShell'
import { staffApi } from '../../api/staff'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatShortDateTimeValue,
  formatStatusLabel,
  toSqlDateTimeValue,
} from '../../lib/format'
import type { AuthSession, MisShareData, MisShareInput, StaffPlanSummary } from '../../types/plans'
import { mapPublicPlanServicesToCompositionRows } from '../../domain/plans/planCompositionRows'
import { PlanCompositionTable } from '../../shared/staff-ui'
import {
  WorkspacePagination,
  WorkspaceSearchPanel,
  WorkspaceSelectFilter,
} from '../shared/WorkspaceListControls'
import {
  workspaceBadgeClassName,
  workspaceEmptyClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
} from '../workspaceStyles'

interface NavigationItem {
  key: string
  label: string
}

const PER_PAGE_OPTIONS = [
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'Все статусы' },
  { value: 'active', label: 'Активные' },
  { value: 'completed', label: 'Завершённые' },
  { value: 'cancelled', label: 'Отменённые' },
]

const SHARING_OPTIONS = [
  { value: 'all', label: 'Все планы' },
  { value: 'with_sharing', label: 'С шерингом' },
  { value: 'without_sharing', label: 'Без шеринга' },
]

function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds} с`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m} мин ${s} с`
}

// ───────────────────────── MisSharePanel ──────────────────────────────────────

function MisSharePanel({
  bootstrap,
  session,
  planId,
  patientId,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  planId: number
  patientId?: number | null
}) {
  const [share, setShare] = useState<MisShareData | null>(null)
  const [loadingShare, setLoadingShare] = useState(true)
  const [input, setInput] = useState<MisShareInput>({
    is_active: false,
    expires_at: null,
    hide_prices: false,
    offer_deadline: null,
    note: '',
    discount_percent: null,
    custom_title: '',
    payment: null,
    access_password: null,
  })
  const [saving, setSaving] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saved' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const loaded = useRef(false)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstInputSet = useRef(true)

  useEffect(() => {
    if (loaded.current) return
    loaded.current = true
    void staffApi.getMisShare(bootstrap, session, planId).then((data) => {
      setShare(data)
      if (data) {
        setInput({
          is_active: data.is_active,
          expires_at: data.expires_at,
          hide_prices: data.hide_prices,
          offer_deadline: data.offer_deadline,
          note: data.note,
          discount_percent: data.discount_percent,
          custom_title: data.custom_title ?? '',
          payment: data.payment ?? null,
          access_password: null,
        })
      }
      setLoadingShare(false)
    })
  }, [bootstrap, session, planId])

  const persistShare = useCallback(async (nextInput: MisShareInput) => {
    try {
      const updated = await staffApi.activateMisShare(bootstrap, session, planId, { ...nextInput, patient_id: patientId ?? null })
      setShare(updated)
      setAutoSaveStatus('saved')
      setTimeout(() => setAutoSaveStatus('idle'), 2000)
      return updated
    } catch {
      setAutoSaveStatus('error')
      return null
    }
  }, [bootstrap, session, planId, patientId])

  // Debounced auto-save (800ms) — persists settings even before activation.
  useEffect(() => {
    if (isFirstInputSet.current) { isFirstInputSet.current = false; return }
    if (loadingShare) return
    setAutoSaveStatus('pending')
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => {
      void persistShare(input)
    }, 800)
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, loadingShare])

  async function handleSave(forceActive = false) {
    // When the share isn't active yet, clicking "Активировать ссылку" must
    // always force is_active=true regardless of the checkbox state.
    const effectiveInput = forceActive || !share?.is_active
      ? { ...input, is_active: true }
      : input
    setSaving(true); setMessage(null)
    try {
      const updated = await staffApi.activateMisShare(bootstrap, session, planId, { ...effectiveInput, patient_id: patientId ?? null })
      setShare(updated)
      setInput((prev) => ({
        ...prev,
        is_active: updated.is_active,
        expires_at: updated.expires_at,
        hide_prices: updated.hide_prices,
        offer_deadline: updated.offer_deadline,
        note: updated.note,
        discount_percent: updated.discount_percent,
        custom_title: updated.custom_title ?? '',
        payment: updated.payment ?? null,
        access_password: null,
      }))
      setMessage('Настройки публикации сохранены.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не удалось сохранить шаринг')
    } finally { setSaving(false) }
  }

  async function handleDeactivate() {
    setSaving(true); setMessage(null)
    try {
      await staffApi.deactivateMisShare(bootstrap, session, planId)
      setShare((prev) => prev ? { ...prev, is_active: false, public_url: null } : null)
      setInput((prev) => ({ ...prev, is_active: false }))
      setMessage('Публичная ссылка деактивирована.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не удалось деактивировать')
    } finally { setSaving(false) }
  }

  const displayPublicUrl = useMemo(() => {
    const raw = share?.public_url ?? null
    if (raw) {
      return raw.replace('?secret=', '?misPlanToken=')
    }
    if (share?.public_token && share.is_active) {
      const host = typeof window !== 'undefined' ? window.location.host : ''
      if (host.startsWith('plans.')) {
        return `https://${host}/?misPlanToken=${encodeURIComponent(share.public_token)}`
      }
    }
    return null
  }, [share?.public_token, share?.public_url, share?.is_active])

  if (loadingShare) {
    return <p className={workspaceMutedTextClassName}>Загружаем настройки шеринга...</p>
  }

  return (
    <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Публичный шеринг</p>
        <h4 className="mt-0.5 text-lg font-semibold text-slate-900">Настройки публикации</h4>
      </div>

      <PublicShareForm
        value={{
          is_active: input.is_active,
          expires_at: input.expires_at,
          hide_prices: input.hide_prices,
          access_password: input.access_password ?? null,
          custom_title: input.custom_title,
          note: input.note,
          discount_percent: input.discount_percent,
          offer_deadline: input.offer_deadline,
          payment: input.payment,
        }}
        onChange={(patch) => setInput((prev) => ({
          ...prev,
          ...(patch.is_active       !== undefined && { is_active:       patch.is_active }),
          ...(patch.expires_at      !== undefined && { expires_at:      patch.expires_at }),
          ...(patch.hide_prices     !== undefined && { hide_prices:     patch.hide_prices }),
          ...(patch.access_password !== undefined && { access_password: patch.access_password }),
          ...(patch.custom_title    !== undefined && { custom_title:    patch.custom_title ?? '' }),
          ...(patch.note            !== undefined && { note:            patch.note ?? '' }),
          ...(patch.discount_percent !== undefined && { discount_percent: patch.discount_percent }),
          ...(patch.offer_deadline  !== undefined && { offer_deadline:  patch.offer_deadline }),
          ...(patch.payment         !== undefined && { payment:         patch.payment }),
        }))}
        showExtendedFields
        publicUrl={displayPublicUrl}
        onExpiresAtCommit={() => void persistShare(input)}
        hasPassword={share?.has_password ?? false}
        analytics={share ? {
          view_count: share.view_count,
          last_viewed_at: share.last_viewed_at,
          last_scroll_depth_percent: share.last_scroll_depth_percent,
          last_read_time_seconds: share.last_read_time_seconds,
        } : null}
        onActivate={() => void handleSave()}
        onDeactivate={() => void handleDeactivate()}
        saving={saving}
        message={message}
        autoSaveStatus={autoSaveStatus}
        bootstrap={bootstrap}
        session={session}
      />
    </div>
  )
}

// ───────────────────────── MisPlanCard ────────────────────────────────────────

function MisPlanCard({
  plan,
  isExpanded,
  onToggle,
  bootstrap,
  session,
  canShare,
}: {
  plan: StaffPlanSummary
  isExpanded: boolean
  onToggle: (planId: number) => void
  bootstrap: TenantBootstrapModel
  session: AuthSession
  canShare: boolean
}) {
  const [detail, setDetail] = useState<PublicTreatmentPlan | 'loading' | 'error' | null>(null)
  const hasLoaded = useRef(false)

  useEffect(() => {
    if (!isExpanded || hasLoaded.current) return
    hasLoaded.current = true
    setDetail('loading')

    void staffApi.getPlanDetail(bootstrap, session, plan.id!)
      .then((d) => setDetail(d))
      .catch(() => setDetail('error'))
  }, [isExpanded, bootstrap, session, plan.id])

  const totalItems = plan.items?.length ?? plan.services?.length ?? plan.total_positions ?? (plan as { items_count?: number }).items_count ?? 0
  const completedItems = (plan.items ?? plan.services ?? []).filter(
    (item) => item.is_executed || item.is_executed_flag
  ).length

  const allServices = useMemo(() => {
    if (!detail || detail === 'loading' || detail === 'error') return null
    if (detail.stages?.length) {
      return detail.stages.flatMap((stage) => stage.services)
    }
    return null
  }, [detail])

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="w-full px-5 py-4 text-left"
        onClick={() => onToggle(plan.id!)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 transition-transform duration-150" style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>▶</span>
              <strong className="truncate text-base text-slate-900">{plan.patient_name ?? 'Пациент не указан'}</strong>
            </div>
            <p className={`${workspaceMutedTextClassName} mt-1`}>
              Врач: {plan.doctor_name ?? '—'} · {formatDate(plan.plan_date ?? plan.created_at)}
              {totalItems > 0 ? ` · ${completedItems}/${totalItems} услуг` : ''}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span className={workspaceBadgeClassName()}>{formatStatusLabel(plan.plan_status ?? plan.status)}</span>
            <strong className="text-lg text-slate-900">{formatCurrency(plan.total_amount)}</strong>
            {plan.has_share ? (
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-100">
                Ссылка активна
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                Без ссылки
              </span>
            )}
          </div>
        </div>
      </button>

      {isExpanded ? (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {detail === 'loading' ? (
            <p className={workspaceMutedTextClassName}>Загружаем детали плана...</p>
          ) : detail === 'error' ? (
            <p className="text-sm text-rose-600">Не удалось загрузить детали плана.</p>
          ) : detail === null ? null : (
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <div className="grid gap-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Состав плана</h4>
                <PlanCompositionTable
                  rows={mapPublicPlanServicesToCompositionRows(allServices ?? [])}
                  completedCount={completedItems}
                  totalCount={totalItems}
                  totalAmount={detail.total_amount}
                  maxHeightClassName="max-h-80"
                />
              </div>

              {canShare && typeof plan.id === 'number' ? (
                <MisSharePanel bootstrap={bootstrap} session={session} planId={plan.id} patientId={plan.patient_id ?? detail.patient_id ?? null} />
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </article>
  )
}

// ───────────────────────── StaffPlansWorkspace ────────────────────────────────

export function StaffPlansWorkspace({
  bootstrap,
  navigation,
  currentView,
  onNavigate,
  session,
  onLogout,
}: {
  bootstrap: TenantBootstrapModel
  navigation: NavigationItem[]
  currentView: string
  onNavigate: (view: string) => void
  session: AuthSession
  onLogout: () => Promise<void> | void
}) {
  const [plans, setPlans] = useState<StaffPlanSummary[]>([])
  const [plansTotal, setPlansTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansMessage, setPlansMessage] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sharingFilter, setSharingFilter] = useState('all')
  const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null)

  const canSharePlans = session.user.permissions.includes('plans_share') || session.user.permissions.includes('users')

  const loadPlans = useCallback(async (
    targetPage = 1,
    targetPerPage = perPage,
    targetSearch: string | null = null,
    targetStatus: string | null = null,
    targetSharing: string | null = null,
  ) => {
    setPlansLoading(true)
    setPlansMessage(null)
    try {
      const effectiveSearch = targetSearch ?? search
      const effectiveStatus = targetStatus ?? statusFilter
      const effectiveSharing = targetSharing ?? sharingFilter
      const payload = await staffApi.getPlans(bootstrap, session, {
        page: targetPage,
        perPage: targetPerPage,
        search: effectiveSearch.trim() ? effectiveSearch.trim() : undefined,
        status: effectiveStatus !== 'all' ? effectiveStatus : undefined,
        sharing: effectiveSharing !== 'all' ? effectiveSharing : undefined,
      })
      const sortByDate = (a: StaffPlanSummary, b: StaffPlanSummary) => {
        const da = a.plan_date ?? a.created_at
        const db = b.plan_date ?? b.created_at
        if (!da && !db) return 0
        if (!da) return 1
        if (!db) return -1
        return new Date(db).getTime() - new Date(da).getTime()
      }
      setPlans([...payload.plans].sort(sortByDate))
      setPlansTotal(payload.total)
      setPage(targetPage)
    } catch (error) {
      setPlans([])
      setPlansMessage(error instanceof Error ? error.message : 'Не удалось загрузить планы')
    } finally {
      setPlansLoading(false)
    }
  }, [bootstrap, session, perPage, search, statusFilter, sharingFilter])

  useEffect(() => {
    void loadPlans(1, perPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.apiBase, session.accessToken, perPage])

  // Серверный поиск + фильтр уже отрабатывают в loadPlans, поэтому здесь
  // оставляем тривиальный pass-through (без локальной фильтрации, иначе
  // мы бы спрятали часть результатов уже отфильтрованной серверной выдачи).
  const filteredPlans = plans

  const totalPages = Math.max(1, Math.ceil(plansTotal / perPage))

  function handleToggleExpand(planId: number) {
    setExpandedPlanId((prev) => (prev === planId ? null : planId))
  }

  function handleSearch() {
    setSearch(searchInput)
    void loadPlans(1, perPage, searchInput, statusFilter, sharingFilter)
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value)
    void loadPlans(1, perPage, search, value, sharingFilter)
  }

  function handleSharingChange(value: string) {
    setSharingFilter(value)
    void loadPlans(1, perPage, search, statusFilter, value)
  }

  function handleResetFilters() {
    setSearch('')
    setSearchInput('')
    setStatusFilter('all')
    setSharingFilter('all')
    void loadPlans(1, perPage, '', 'all', 'all')
  }

  function handlePageChange(direction: -1 | 1) {
    const nextPage = Math.min(Math.max(page + direction, 1), totalPages)
    if (nextPage === page) return
    void loadPlans(nextPage, perPage, search, statusFilter, sharingFilter)
  }

  function handlePerPageChange(value: string) {
    const n = parseInt(value, 10)
    if (!Number.isNaN(n)) {
      setPerPage(n)
    }
  }

  return (
    <StaffShell
      title="Планы МИС"
      subtitle={`Планы лечения с публичным шерингом · ${plansTotal.toLocaleString('ru-RU')} планов в МИС`}
      navigation={navigation}
      currentView={currentView}
      onNavigate={onNavigate}
      session={session}
      bootstrapTenant={bootstrap.tenant}
      onRefresh={() => void loadPlans(page, perPage, search, statusFilter, sharingFilter)}
      onLogout={() => void onLogout()}
    >
      <div className="grid gap-5">
        {/* Search + filters + per-page */}
        <section className={workspacePanelClassName}>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px_180px_180px]">
            <WorkspaceSearchPanel
              label="Поиск"
              value={searchInput}
              placeholder="Название плана, пациент, врач..."
              onChange={setSearchInput}
              onSubmit={handleSearch}
            />
            <WorkspaceSelectFilter
              label="Статус"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={handleStatusChange}
            />
            <WorkspaceSelectFilter
              label="Шеринг"
              value={sharingFilter}
              options={SHARING_OPTIONS}
              onChange={handleSharingChange}
            />
            <WorkspaceSelectFilter
              label="Показывать"
              value={String(perPage)}
              options={PER_PAGE_OPTIONS}
              onChange={handlePerPageChange}
            />
          </div>

          <div className="mt-1 flex items-center justify-between gap-3 text-sm text-slate-500">
            <span>
              {plansLoading
                ? 'Загружаем...'
                : plansMessage
                  ?? `Найдено: ${filteredPlans.length} ${filteredPlans.length !== plans.length ? `из ${plans.length}` : ''}`
              }
            </span>
            <span className="shrink-0">Всего в МИС: {plansTotal}</span>
          </div>
        </section>

        {/* Plan cards list */}
        <section className="grid gap-3">
          {plansLoading && plans.length === 0 ? (
            <div className={workspaceEmptyClassName}>
              <p>Загружаем планы...</p>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className={workspaceEmptyClassName}>
              <p>{search || statusFilter !== 'all' || sharingFilter !== 'all' ? 'Нет планов по заданным фильтрам.' : 'Нет доступных планов.'}</p>
              {(search || statusFilter !== 'all' || sharingFilter !== 'all') ? (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="mt-3 inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Сбросить фильтры
                </button>
              ) : null}
            </div>
          ) : (
            filteredPlans.map((plan) => (
              <MisPlanCard
                key={plan.id ?? plan.uid ?? `${plan.title}-${plan.patient_name}`}
                plan={plan}
                isExpanded={expandedPlanId === plan.id}
                onToggle={handleToggleExpand}
                bootstrap={bootstrap}
                session={session}
                canShare={canSharePlans}
              />
            ))
          )}
        </section>

        {/* Pagination */}
        {!plansLoading && plansTotal > perPage ? (
          <section className={workspacePanelClassName}>
            <WorkspacePagination
              page={page}
              pages={totalPages}
              total={plansTotal}
              pageSize={perPage}
              itemLabel="планов"
              onPrevPage={() => handlePageChange(-1)}
              onNextPage={() => handlePageChange(1)}
            />
          </section>
        ) : null}
      </div>
    </StaffShell>
  )
}
